import React from 'react';
import {
  Stack,
  Button,
  IconButton,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  Text,
  Divider,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdEdit, MdSave, MdClose } from 'react-icons/md';
import { NEARBY } from '@habity/constants';
import { NearbyType } from '@habity/types';
import { NearbyEditItem } from '~/hooks/useNearbyEditor';

type NearbyEditorProps = {
  isEditing: boolean;
  isSaving: boolean;
  editItems: NearbyEditItem[];
  canEdit: boolean;
  maxDistance?: number; // Maximum allowed distance in meters
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onAddItem: () => void;
  onRemoveItem: (id: string) => void;
  onUpdateItem: (id: string, updates: Partial<NearbyEditItem>) => void;
};

export const NearbyEditor: React.FC<NearbyEditorProps> = ({
  isEditing,
  isSaving,
  editItems,
  canEdit,
  maxDistance,
  onEdit,
  onCancel,
  onSave,
  onAddItem,
  onRemoveItem,
  onUpdateItem,
}) => {
  // Check if any item exceeds max distance
  const hasInvalidDistance = maxDistance !== undefined && maxDistance !== Infinity
    ? editItems.some(item => item.distance >= maxDistance)
    : false;
  if (!canEdit && !isEditing) {
    return null;
  }

  if (!isEditing) {
    return (
      <Box>
        <Button
          leftIcon={<MdEdit />}
          size="sm"
          variant="ghost"
          onClick={onEdit}
        >
          Redigera närliggande platser
        </Button>
      </Box>
    );
  }

  return (
    <Stack spacing={4} p={4} bg="gray.50" borderRadius="md">
      <HStack justify="space-between">
        <Text fontWeight="semibold" fontSize="md">
          Redigera närliggande platser
        </Text>
        <HStack>
          <Button
            leftIcon={<MdSave />}
            size="sm"
            colorScheme="green"
            onClick={onSave}
            isLoading={isSaving}
            isDisabled={
              editItems.some(item => !item.name || !item.type) || 
              hasInvalidDistance
            }
          >
            Spara
          </Button>
          <Button
            leftIcon={<MdClose />}
            size="sm"
            variant="ghost"
            onClick={onCancel}
            isDisabled={isSaving}
          >
            Avbryt
          </Button>
        </HStack>
      </HStack>

      <Divider />

      <Stack spacing={3}>
        {editItems.map((item, index) => (
          <Box
            key={item.id}
            p={3}
            bg="white"
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.200"
          >
            <Stack spacing={3}>
              <HStack justify="space-between" align="flex-start">
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  Plats #{index + 1}
                </Text>
                <IconButton
                  aria-label="Ta bort plats"
                  icon={<MdDelete />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => onRemoveItem(item.id)}
                />
              </HStack>

              <HStack spacing={3} align="flex-start">
                <FormControl isRequired flex="2">
                  <FormLabel fontSize="sm">Typ</FormLabel>
                  <Select
                    size="sm"
                    value={item.type || ''}
                    onChange={(e) =>
                      onUpdateItem(item.id, { type: e.target.value as NearbyType })
                    }
                    placeholder="Välj typ..."
                  >
                    {NEARBY.map((nearby) => (
                      <option key={nearby.key} value={nearby.key}>
                        {nearby.title}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired flex="3">
                  <FormLabel fontSize="sm">Namn</FormLabel>
                  <Input
                    size="sm"
                    value={item.name}
                    onChange={(e) =>
                      onUpdateItem(item.id, { name: e.target.value })
                    }
                    placeholder="T.ex. ICA Supermarket"
                  />
                </FormControl>

                <FormControl 
                  isRequired 
                  flex="2"
                  isInvalid={
                    maxDistance !== undefined && 
                    maxDistance !== Infinity && 
                    item.distance >= maxDistance
                  }
                >
                  <FormLabel fontSize="sm">
                    Avstånd (m)
                    {maxDistance !== undefined && maxDistance !== Infinity && (
                      <Text as="span" fontSize="xs" color="gray.500" ml={1}>
                        (max {maxDistance}m)
                      </Text>
                    )}
                  </FormLabel>
                  <NumberInput
                    size="sm"
                    value={item.distance}
                    onChange={(_, value) =>
                      onUpdateItem(item.id, { distance: value })
                    }
                    min={0}
                    max={maxDistance !== undefined && maxDistance !== Infinity ? maxDistance - 1 : 10000}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  {maxDistance !== undefined && 
                   maxDistance !== Infinity && 
                   item.distance >= maxDistance && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                      Avståndet måste vara mindre än {maxDistance}m
                    </Text>
                  )}
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">Adress (valfritt)</FormLabel>
                <Input
                  size="sm"
                  value={item.address || ''}
                  onChange={(e) =>
                    onUpdateItem(item.id, { address: e.target.value })
                  }
                  placeholder="T.ex. Storgatan 1, Stockholm"
                />
              </FormControl>
            </Stack>
          </Box>
        ))}
      </Stack>

      <Button
        leftIcon={<MdAdd />}
        size="sm"
        variant="outline"
        onClick={onAddItem}
      >
        Lägg till plats
      </Button>
    </Stack>
  );
};
